
import * as THREE from 'three';
import { updateWaveAnimation, updateEmitters, createEmitter, Emitter } from '../waveUtils';

interface AnimationOptions {
  particleCount: number;
  waveWidth: number;
  waveDepth: number;
  maxEmitters: number;
  emitterChance: number;
}

/**
 * Create animation loop function for the data wave
 */
export const createAnimationLoop = (
  sceneRef: React.MutableRefObject<THREE.Scene | null>,
  cameraRef: React.MutableRefObject<THREE.PerspectiveCamera | null>,
  rendererRef: React.MutableRefObject<THREE.WebGLRenderer | null>,
  geometryRef: React.MutableRefObject<THREE.BufferGeometry | null>,
  positionsRef: React.MutableRefObject<Float32Array | null>,
  frameIdRef: React.MutableRefObject<number | null>,
  clockRef: React.MutableRefObject<THREE.Clock>,
  emittersRef: React.MutableRefObject<Emitter[]>,
  mousePosition: { x: number, y: number },
  options: AnimationOptions
) => {
  const animate = () => {
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current || 
        !geometryRef.current || !positionsRef.current) {
      return;
    }

    const delta = clockRef.current.getDelta();
    const elapsedTime = clockRef.current.getElapsedTime();

    // Original wave animation parameters
    const amplitude = 0.6; 
    const frequency = 0.8;
    const speed = 0.5;

    // Update wave animation
    updateWaveAnimation(
      positionsRef.current,
      options.particleCount,
      elapsedTime,
      amplitude,
      frequency,
      speed,
      mousePosition.x,
      mousePosition.y
    );

    // Update emitters
    updateEmitters(emittersRef.current, delta);

    // Randomly create new emitters
    if (
      emittersRef.current.length < options.maxEmitters && 
      Math.random() < options.emitterChance
    ) {
      emittersRef.current.push(
        createEmitter(
          options.waveWidth,
          options.waveDepth,
          amplitude,
          frequency,
          elapsedTime,
          speed
        )
      );
    }

    // Update buffers
    geometryRef.current.attributes.position.needsUpdate = true;

    // Render scene
    rendererRef.current.render(sceneRef.current, cameraRef.current);

    // Continue animation loop
    frameIdRef.current = requestAnimationFrame(animate);
  };

  return animate;
};
